import { Textarea } from "@component/textarea";
import { Label } from "@component/label";
import { BookOpen, Info } from "lucide-react";
import { InterviewAssessment } from "@type/assessments";
import { renderTextWithLineBreaks } from "@util/format";

interface Props {
    mode: "view" | "edit";
    data: InterviewAssessment;
    onDataChange: (data: InterviewAssessment) => void;
}

export default function InterviewKnowledgeBaseSection({
    mode,
    data,
    onDataChange,
}: Props) {

    const handleFieldChange = (
        field: string,
        value: string | object
    ) => {
        onDataChange({
            ...data,
            content: {
                ...data.content,
                [field]: value,
            },
        });
    };

    return (
        <div
            className="pb-8 border-b border-gray-200"
            style={{ paddingTop: "24px", paddingBottom: "40px" }}
        >
            {/* Two-Panel Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* Left Panel: Header and Description */}
                <div className="lg:col-span-3 flex flex-col space-y-4">
                    <div className="space-y-3">
                        <div className="flex items-center justify-between gap-3 pr-[5px]">
                            <div
                                className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30"
                                style={{ backgroundColor: "#dbeafe", color: "#2563eb" }}
                            >
                                <BookOpen
                                    className="w-5 h-5 text-blue-600 dark:text-blue-400"
                                    style={{ color: "#2563eb" }}
                                />
                            </div>
                            <div className="flex-1">
                                <div className="flex items-start justify-between gap-2">
                                    <h2 className="text-lg font-semibold">
                                        Interview Knowledge <br /> Base
                                    </h2>
                                    <span
                                        className="inline-flex items-center px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded mt-0.5"
                                        style={{
                                            paddingTop: "4px",
                                            paddingBottom: "4px",
                                            borderRadius: "1rem",
                                        }}
                                    >
                                        Step 3
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <p className="text-sm text-muted-foreground">
                        Give your AI interviewer the background it needs to handle candidate
                        questions confidently and consistently.
                    </p>
                </div>

                {/* Right Panel: Input Fields */}
                <div className="lg:col-span-9 space-y-6">
                    {/* Information to Share with Candidates */}
                    <div className="space-y-2">
                        <Label className="text-base font-medium flex items-center gap-2">
                            <Info className="w-4 h-4 text-blue-600" />
                            Information for Candidate Questions
                        </Label>
                        {mode === "edit" && (
                            <p className="text-xs text-gray-500">
                                Provide reference notes the AI can use for questions from
                                candidates. If left blank, the AI will provide a generic
                                response: "Your inquiry has been received and will be forwarded
                                to the hiring team."
                            </p>
                        )}
                        {mode === "edit" ? (
                            <Textarea
                                value={data.content.informationToShare?.value || ""}
                                onChange={(e) => {
                                    handleFieldChange("informationToShare", {
                                        enabled: true,
                                        value: e.target.value,
                                    });
                                }}
                                placeholder="Provide safe to share details such as work expectations, culture, team context, timeline, or benefits. Avoid internal discussion, confidential plans or sensitive company information."
                                className="min-h-[120px] resize-y"
                            />
                        ) : (
                            <div className="text-sm py-2 px-3 bg-gray-50 border border-gray-200 rounded-md min-h-[120px]">
                                <span
                                    className={
                                        data.content.informationToShare?.value
                                            ? "text-gray-900"
                                            : "text-gray-500"
                                    }
                                    style={
                                        !data.content.informationToShare?.value
                                            ? { color: "#9ca3af" }
                                            : undefined
                                    }
                                >
                                    {renderTextWithLineBreaks(data.content.informationToShare?.value)}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
